import { endOfDay, getWeek, startOfMonth } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import EasePicker, { AmpPlugin, PresetPlugin, RangePlugin } from "react-easepick";
import { Helmet, HelmetProvider } from "react-helmet-async";
// import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import api from "../api";
import { CourseType } from "../common/constants";
// import MainContext from "../common/MainContext";
import StatusBadge from "../components/StatusBadge";
import Breadcrumb from "../components/Breadcrumb";
import Button from "../components/Button";
import Card from "../components/cards/Card";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import MultipleSelect from "../components/MultipleSelect";
import Select from "../components/Select";
import Table from "../components/Table";
import TextInput from "../components/TextInput";

import { formatDateV2, formatPrice } from "../utils";
import styles from "./Reports.module.css";
import Skeleton from "../components/Skeleton";
import { ReactComponent as ExportIcon } from "../assets/images/icons/ic-export.svg";


// Small debounce hook to delay value updates until user stops changing input
function useDebounce(value, delay = 400) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

function isAbortError(error) {
  return error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError';
}

function isVisibleTransactionStatus(status) {
  return ['succeeded', 'refunded', 'partially_refunded', 'waiting_for_refund'].includes(status);
}

function normalizeTransactionStatus(status) {
  if (!status) return '';
  return typeof status === 'string' ? status.toLowerCase() : String(status).toLowerCase();
}

function padNumber(value) {
  return String(value).padStart(2, '0');
}

function getPeriodStart(dateValue, period) {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  if (period === 'week') {
    const weekStart = new Date(date);
    const day = weekStart.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    weekStart.setDate(weekStart.getDate() + diff);
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
  }

  if (period === 'month') {
    return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
  }

  if (period === 'year') {
    return new Date(date.getFullYear(), 0, 1, 0, 0, 0, 0);
  }

  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function getNextPeriodStart(dateValue, period) {
  const date = new Date(dateValue);

  if (period === 'week') {
    date.setDate(date.getDate() + 7);
  } else if (period === 'month') {
    date.setMonth(date.getMonth() + 1, 1);
  } else if (period === 'year') {
    date.setFullYear(date.getFullYear() + 1, 0, 1);
  } else {
    date.setDate(date.getDate() + 1);
  }

  return date;
}

function getPeriodKey(dateValue, period) {
  const date = getPeriodStart(dateValue, period);
  if (!date) return null;

  if (period === 'month') {
    return `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}`;
  }

  if (period === 'year') {
    return `${date.getFullYear()}`;
  }

  return `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}`;
}

function createEmptyStatsBucket(date) {
  return {
    date,
    count: 0,
    amount: 0,
    progressive_amount: 0,
    pending_amount: 0,
    pending_progressive_amount: 0,
    abandoned_amount: 0,
    abandoned_progressive_amount: 0,
    total_amount: 0,
    total_progressive_amount: 0,
  };
}

function createPeriodSeries(rangeStart, rangeEnd, period = 'day') {
  const grouped = new Map();

  if (!rangeStart || !rangeEnd) {
    return grouped;
  }

  let cursor = getPeriodStart(rangeStart, period);
  const end = getPeriodStart(rangeEnd, period);

  if (!cursor || !end) {
    return grouped;
  }

  while (cursor <= end) {
    const periodKey = getPeriodKey(cursor, period);
    grouped.set(periodKey, createEmptyStatsBucket(periodKey));
    cursor = getNextPeriodStart(cursor, period);
  }

  return grouped;
}

function aggregateTransactionsByPeriod(transactions, period = 'day', rangeStart = null, rangeEnd = null) {
  const grouped = createPeriodSeries(rangeStart, rangeEnd, period);

  for (const transaction of transactions || []) {
    const periodKey = getPeriodKey(transaction?.date, period);
    if (!periodKey) continue;

    const status = normalizeTransactionStatus(transaction?.status);
    const amount = Number(Number(transaction?.amount || 0).toFixed(2));

    if (!grouped.has(periodKey)) {
      grouped.set(periodKey, createEmptyStatsBucket(periodKey));
    }

    const current = grouped.get(periodKey);
    current.count += 1;

    if (status === 'pending') {
      current.pending_amount += amount;
      current.total_amount += amount;
    } else if (status === 'abandoned') {
      current.abandoned_amount += amount;
    } else if (['succeeded', 'refunded', 'partially_refunded', 'waiting_for_refund'].includes(status)) {
      current.amount += amount;
      current.total_amount += amount;
    }
  }

  let progressiveAmount = 0;
  let progressivePendingAmount = 0;
  let progressiveAbandonedAmount = 0;
  let progressiveTotalAmount = 0;

  return Array.from(grouped.values())
    .sort((a, b) => a.date.localeCompare(b.date))
    .map((item) => {
      progressiveAmount += item.amount;
      progressivePendingAmount += item.pending_amount;
      progressiveAbandonedAmount += item.abandoned_amount;
      progressiveTotalAmount += item.total_amount;

      return {
        ...item,
        amount: Number(item.amount.toFixed(2)),
        progressive_amount: Number(progressiveAmount.toFixed(2)),
        pending_amount: Number(item.pending_amount.toFixed(2)),
        pending_progressive_amount: Number(progressivePendingAmount.toFixed(2)),
        abandoned_amount: Number(item.abandoned_amount.toFixed(2)),
        abandoned_progressive_amount: Number(progressiveAbandonedAmount.toFixed(2)),
        total_amount: Number(item.total_amount.toFixed(2)),
        total_progressive_amount: Number(progressiveTotalAmount.toFixed(2)),
      };
    });
}

function alignSeriesByIndex(actualSeries, previousSeries) {
  return actualSeries.map((item, index) => {
    const previousItem = previousSeries[index] || previousSeries[previousSeries.length - 1] || createEmptyStatsBucket(item.date);
    return {
      ...previousItem,
      date: item.date,
      compare_date: previousItem.date,
    };
  });
}

function getPreviousDateRange(startDate, endDate) {
  if (!startDate || !endDate) return null;

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return null;
  }

  const duration = end.getTime() - start.getTime();
  const previousEnd = new Date(start.getTime() - 1);
  const previousStart = new Date(previousEnd.getTime() - duration);

  return {
    start: previousStart,
    end: previousEnd,
  };
}

function buildSalesStatsFromTransactions(actualTransactions, previousTransactions = [], period = 'day', actualRange = {}, previousRange = {}) {
  const actual = aggregateTransactionsByPeriod(actualTransactions, period, actualRange?.start, actualRange?.end);
  const previous = alignSeriesByIndex(actual, aggregateTransactionsByPeriod(previousTransactions, period, previousRange?.start, previousRange?.end));

  return {
    actual,
    previous,
  };
}

const Reports = () => {
  // const { t } = useTranslation();
  const navigate = useNavigate()

  const [startDate, setStartDate] = useState(startOfMonth(new Date()))
  const [endDate, setEndDate] = useState(endOfDay(new Date()))
  const [salesPeriod, setSalesPeriod] = useState('day')
  const [salesView, setSalesView] = useState('trend')

  const [salesStats, setSalesStats] = useState({})
  const [sales, setSales] = useState([])
  const [previousSales, setPreviousSales] = useState([])
  const [filteredSales, setFilteredSales] = useState([])

  const [productSearch, setProductSearch] = useState("")
  const [courses, setCourses] = useState([])
  const [selectedProducts, setSelectedProducts] = useState([])
  const [test, setTest] = useState(false)
  const [currentSort, setCurrentSort] = useState({ key: 'date', direction: 'desc' });
  const [loading, setLoading] = useState({ transactions: false, stats: false, courses: false });
  const [expandedPackRows, setExpandedPackRows] = useState({});
  const [packBreakdownByRow, setPackBreakdownByRow] = useState({});
  const [packBreakdownLoadingByRow, setPackBreakdownLoadingByRow] = useState({});
  const [packBreakdownErrorByRow, setPackBreakdownErrorByRow] = useState({});
  // const [pinnedColumn, setPinnedColumn] = useState(null);

  // Debounced version of selectedProducts to avoid spamming API calls on rapid changes
  const debouncedStartDate = useDebounce(startDate, 500);
  const debouncedEndDate = useDebounce(endDate, 500);
  const debouncedSalesPeriod = useDebounce(salesPeriod, 500);
  const debouncedTest = useDebounce(test, 500);
  const debouncedSelectedProducts = useDebounce(selectedProducts, 500);

  const options = useMemo(
    () => ({
      css: [
        'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css',
        'https://cdn.jsdelivr.net/npm/@easepick/range-plugin@1.2.1/dist/index.css',
        '/css/easepick-range.css',
      ],
      zIndex: 1000,
      plugins: [
        RangePlugin,
        PresetPlugin,
        AmpPlugin
      ],
      lang: 'it-IT',
      format: "DD MMM YYYY",
      AmpPlugin: {
        resetButton: () => {
          setStartDate(null)
          setEndDate(null)
          return true
        },
        darkMode: false,
        dropdown: {
          months: true,
          years: true,
          minYear: 2020
        }
      },
      PresetPlugin: {
        customPreset: {
          'Oggi': [
            (() => {
              const date = new Date();
              return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
            })(),
            (() => {
              const date = new Date();
              return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
            })(),
          ],
          '7 giorni': [
            (() => {
              const date = new Date();
              date.setDate(date.getDate() - 6); // 6 giorni fa
              return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
            })(),
            (() => {
              const date = new Date();
              return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
            })(),
          ],
          'Settimana': [
            (() => {
              const now = new Date();
              const day = now.getDay(); // 0 (domenica) - 6 (sabato)
              const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Lunedì
              const monday = new Date(now.setDate(diff));
              return new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 0, 0, 0, 0);
            })(),
            (() => {
              const date = new Date();
              return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
            })(),
          ],
          '30 giorni': [
            (() => {
              const date = new Date();
              date.setDate(date.getDate() - 29); // 29 giorni fa
              return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
            })(),
            (() => {
              const date = new Date();
              return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
            })(),
          ],
          'Mese': [
            (() => {
              const now = new Date();
              return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0); // Primo giorno del mese
            })(),
            (() => {
              const now = new Date();
              return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999); // Ultimo giorno del mese
            })(),
          ],
          'Anno': [
            new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0),
            new Date(new Date().getFullYear(), 11, 31, 23, 59, 59, 999),
          ]
        }
      },
      setup(picker) {
        picker.on("select", (e) => {
          const { start, end } = e.detail;
          setStartDate(start)
          end.setHours(23, 59, 59, 999)
          setEndDate(end)
        });
      },
    }),
    []
  );

  const products = useMemo(() => {
    if (courses.length === 0) return []

    return courses.filter(course => course.product).map((course) => {
      return {
        id: course.product.id,
        name: course.name,
        value: course.product.id
      }
    }).sort((a, b) => {
      return a.name.localeCompare(b.name)
    })
  }, [courses])

  const filteredProducts = useMemo(() => {
    const query = (productSearch || "").trim().toLowerCase()
    if (!query) return products

    return products.filter((product) =>
      (product?.name || "").toLowerCase().includes(query)
    )
  }, [products, productSearch])

  const salesViewOptions = useMemo(() => ([
    { id: 'trend', name: 'Progressivo', value: 'trend' },
    { id: 'volumes', name: 'Volume', value: 'volumes' },
  ]), [])

  const salesPeriodOptions = useMemo(() => ([
    { id: 'day', name: 'Giornaliero', value: 'day' },
    { id: 'week', name: 'Settimanale', value: 'week' },
    { id: 'month', name: 'Mensile', value: 'month' },
    { id: 'year', name: 'Annuale', value: 'year' },
  ]), [])

  const getSales = useCallback(async (signal) => {
    setLoading(prev => ({ ...prev, transactions: true, stats: true }))
    try {
      const buildTransactionParams = (rangeStart, rangeEnd) => {
        const params = new URLSearchParams();

        if (rangeStart) {
          params.append("start_date", new Date(rangeStart).toISOString());
        }
        if (rangeEnd) {
          params.append("end_date", new Date(rangeEnd).toISOString());
        }
        if (debouncedSelectedProducts.length > 0) {
          params.append("product_ids", debouncedSelectedProducts.join(","))
        }
        if (debouncedTest) {
          params.append("test", debouncedTest);
        }

        return params;
      };

      const previousRange = getPreviousDateRange(debouncedStartDate, debouncedEndDate);

      const [currentTransactions, previousTransactions] = await Promise.all([
        api.get(`/admin/reports/sales/transactions?${buildTransactionParams(debouncedStartDate, debouncedEndDate).toString()}`, { signal }),
        previousRange
          ? api.get(`/admin/reports/sales/transactions?${buildTransactionParams(previousRange.start, previousRange.end).toString()}`, { signal })
          : Promise.resolve([]),
      ]);

      if (signal?.aborted) return;
      setSales(currentTransactions)
      setPreviousSales(previousTransactions)
      setFilteredSales(currentTransactions)
    }
    catch (error) {
      if (isAbortError(error)) return;
      console.error(error);
    }
    finally {
      if (signal?.aborted) return;
      setLoading(prev => ({ ...prev, transactions: false, stats: false }))
    }
  }, [debouncedStartDate, debouncedEndDate, debouncedSelectedProducts, debouncedTest])

  useEffect(() => {
    const getCourses = async () => {
      setLoading(prev => ({ ...prev, courses: true }))
      try {
        const courses = await api.get("/admin/courses")
        setCourses(courses)
      }
      catch (e) {
        console.error(e)
      }
      finally {
        setLoading(prev => ({ ...prev, courses: false }))
      }
    }

    getCourses()
  }, [])

  useEffect(() => {
    const controller = new AbortController();
    getSales(controller.signal)
    return () => controller.abort();
  }, [getSales])

  useEffect(() => {
    const previousRange = getPreviousDateRange(debouncedStartDate, debouncedEndDate);
    setSalesStats(buildSalesStatsFromTransactions(
      sales,
      previousSales,
      debouncedSalesPeriod,
      { start: debouncedStartDate, end: debouncedEndDate },
      previousRange || {},
    ));
  }, [sales, previousSales, debouncedSalesPeriod, debouncedStartDate, debouncedEndDate])

  const formatDate = (date, period) => {
    if (period === 'day') {
      return formatDateV2(date, { day: '2-digit', month: 'long', year: 'numeric' })
    } else if (period === 'week') {
      let weekNumber = getWeek(new Date(date))
      let year = new Date(date).getFullYear()
      return 'Settimana ' + weekNumber + ' - ' + year
    } else if (period === 'month') {
      return formatDateV2(date, { month: 'long', year: 'numeric' })
    } else if (period === 'year') {
      return formatDateV2(date, { year: 'numeric' })
    }
  }

  const getTypeBadge = (type) => {
    switch (type) {
      case 'order':
        return {
          label: 'Ordine',
          name: 'Ordine',
          color: 'var(--sf-green)'
        }
      case 'refund':
        return {
          label: 'Rimborso',
          name: 'Rimborso',
          color: 'var(--sf-red)'
        }
      case 'installment':
        return {
          label: 'Rata',
          name: 'Rata',
          color: 'var(--sf-orange)'
        }
      default:
        return null;
    }
  }

  const suceededSalesPercentageDifference = useMemo(() => {
    if (!salesStats?.actual || !salesStats?.previous || salesStats.actual.length === 0 || salesStats.previous.length === 0) {
      return null;
    }

    const actualTotal = salesStats.actual[salesStats.actual.length - 1].progressive_amount;
    const previousTotal = salesStats.previous[salesStats.previous.length - 1].progressive_amount;

    if (previousTotal === 0) {
      return actualTotal > 0 ? Infinity : actualTotal < 0 ? -Infinity : 0;
    } else {
      return ((actualTotal - previousTotal) / Math.abs(previousTotal)) * 100;
    }
  }, [salesStats?.actual, salesStats?.previous]);

  const totalSalesPercentageDifference = useMemo(() => {
    if (!salesStats?.actual || !salesStats?.previous || salesStats.actual.length === 0 || salesStats.previous.length === 0) {
      return null;
    }

    const actualTotal = salesStats.actual[salesStats.actual.length - 1].total_progressive_amount;
    const previousTotal = salesStats.previous[salesStats.previous.length - 1].total_progressive_amount;

    if (previousTotal === 0) {
      return actualTotal > 0 ? Infinity : actualTotal < 0 ? -Infinity : 0;
    } else {
      return ((actualTotal - previousTotal) / Math.abs(previousTotal)) * 100;
    }
  }, [salesStats?.actual, salesStats?.previous]);


  const pendingSalesPercentageDifference = useMemo(() => {
    if (!salesStats?.actual || !salesStats?.previous || salesStats.actual.length === 0 || salesStats.previous.length === 0) {
      return null;
    }

    const actualTotal = salesStats.actual[salesStats.actual.length - 1].pending_progressive_amount;
    const previousTotal = salesStats.previous[salesStats.previous.length - 1].pending_progressive_amount;

    if (previousTotal === 0) {
      return actualTotal > 0 ? Infinity : actualTotal < 0 ? -Infinity : 0;
    } else {
      return ((actualTotal - previousTotal) / Math.abs(previousTotal)) * 100;
    }
  }, [salesStats?.actual, salesStats?.previous]);

  const abandonedSalesPercentageDifference = useMemo(() => {
    if (!salesStats?.actual || !salesStats?.previous || salesStats.actual.length === 0 || salesStats.previous.length === 0) {
      return null;
    }

    const actualTotal = salesStats.actual[salesStats.actual.length - 1].abandoned_progressive_amount;
    const previousTotal = salesStats.previous[salesStats.previous.length - 1].abandoned_progressive_amount;

    if (previousTotal === 0) {
      return actualTotal > 0 ? Infinity : actualTotal < 0 ? -Infinity : 0;
    } else {
      return ((actualTotal - previousTotal) / Math.abs(previousTotal)) * 100;
    }
  }, [salesStats?.actual, salesStats?.previous]);



  const CustomTooltip = ({ active, payload, type = 'raw' }) => {
    if (active && payload && payload.length) {
      const actualData = payload[0];
      const previousData = payload[1];

      let percentageDifference = NaN;
      if (previousData) {
        if (previousData.value === 0) {
          percentageDifference = actualData.value > 0 ? Infinity : actualData.value < 0 ? -Infinity : 0;
        } else {
          percentageDifference = ((actualData.value - previousData.value) / Math.abs(previousData.value)) * 100;
        }
      }

      if (percentageDifference === Infinity || isNaN(percentageDifference) || percentageDifference === -Infinity || percentageDifference === 0) {
        percentageDifference = null;
      }


      return (
        <div style={{ backgroundColor: "#fff", padding: "10px", borderRadius: "8px", boxShadow: "0 0 10px rgba(0,0,0,0.1)" }}>
          {actualData && (
            <div className={styles.tooltipRow}>
              <div className={styles.tooltipDate} style={{ color: "var(--primary)" }}>
                {formatDate(actualData.payload.date, salesPeriod)}
              </div>
              <div className={styles.tooltipValue}>
                <p style={{ margin: 0 }}>
                  {type === 'price' &&
                    `€ ${formatPrice(actualData.value)}`
                  }
                  {type === 'raw' &&
                    actualData.value
                  }
                </p>
                {percentageDifference &&
                  <div className={`${styles.precentageBadge} ${percentageDifference < 0 ? styles.loss : ''}`}>
                    {percentageDifference > 0 ? '+' : ''}{percentageDifference.toFixed(0)}%
                  </div>
                }
              </div>
            </div>
          )}
          {previousData && (
            <div className={`${styles.tooltipRow} ${styles.previous}`}>
              <div className={styles.tooltipDate} style={{ color: "rgba(var(--text-color-rgb), .6)" }}>
                {formatDate(previousData.payload.compare_date || previousData.payload.date, salesPeriod)}
              </div>
              <div className={styles.tooltipValue}>
                <p style={{ margin: 0 }}>
                  {type === 'price' &&
                    `€ ${formatPrice(previousData.value)}`
                  }
                  {type === 'raw' &&
                    previousData.value
                  }
                </p>
              </div>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const exportSalesToCSV = async () => {
    if (!filteredSales || filteredSales.length === 0) {
      alert("Nessuna vendita da esportare.");
      return;
    }

    const sanitizeCsvProductName = (name) => {
      if (!name) {
        return "";
      }
      return String(name)
        .replaceAll(",", " ")
        .replace(/\s+/g, " ")
        .trim();
    };

    const toFiniteNumber = (value, fallback = 0) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : fallback;
    };

    const getSaleTypeLabel = (sale) => {
      if (sale?.type === "refund") {
        return "Rimborso";
      }
      if (sale?.type === "installment") {
        return "Rata";
      }
      return "Ordine";
    };

    const getSaleMethodLabel = (sale) => {
      const rawMethod = String(sale?.payment_method || "").trim().toLowerCase();
      if (rawMethod) {
        if (rawMethod === "customer_balance") {
          return "Bonifico";
        }
        return rawMethod
          .split("_")
          .map((part) => (part ? `${part.charAt(0).toUpperCase()}${part.slice(1)}` : ""))
          .join(" ");
      }

      return sale?.transaction_id ? "Stripe" : "Manuale";
    };

    const getHistoricalPriceSourceLabel = (source) => {
      if (source === "products_prices") {
        return "Storico prezzi";
      }
      if (source === "product_fallback") {
        return "Prezzo prodotto attuale (fallback)";
      }
      return source || "";
    };

    const escapeCsv = (value) => {
      const normalized = String(value ?? "");
      if (/[",\n]/.test(normalized)) {
        return `"${normalized.replace(/"/g, '""')}"`;
      }
      return normalized;
    };

    const getSaleRowKey = (sale) => `${sale.order_id}-${sale.product_id}-${sale.type}-${sale.transaction_id || 'na'}`;

    const productCourseMap = new Map(
      (courses || [])
        .filter((course) => course?.product?.id)
        .map((course) => [course.product.id, course.id])
    );

    const visibleSales = filteredSales.filter((sale) => isVisibleTransactionStatus(sale.status));
    const packSales = visibleSales.filter((sale) => sale?.is_pack);
    const fetchedBreakdowns = {};

    if (packSales.length > 0) {
      const breakdownEntries = await Promise.all(packSales.map(async (sale) => {
        const rowKey = getSaleRowKey(sale);

        if (packBreakdownByRow[rowKey]) {
          return [rowKey, packBreakdownByRow[rowKey]];
        }

        try {
          const params = new URLSearchParams({
            order_id: String(sale.order_id),
            product_id: String(sale.product_id),
          });
          const response = await api.get(`/admin/reports/sales/transactions/pack-breakdown?${params.toString()}`);
          return [rowKey, response];
        } catch (error) {
          console.error(error);
          return [rowKey, null];
        }
      }));

      breakdownEntries.forEach(([rowKey, response]) => {
        if (rowKey && response) {
          fetchedBreakdowns[rowKey] = response;
        }
      });

      if (Object.keys(fetchedBreakdowns).length > 0) {
        setPackBreakdownByRow((prev) => ({ ...prev, ...fetchedBreakdowns }));
      }
    }

    const getCourseIdsForSale = (sale) => {
      const directCourseIds = Array.isArray(sale?.course_ids)
        ? sale.course_ids
        : (sale?.course_id ? [sale.course_id] : []);

      if (directCourseIds.length > 0) {
        return [...new Set(directCourseIds.map((id) => String(id)))].join(' - ');
      }

      const rowKey = getSaleRowKey(sale);
      const breakdown = fetchedBreakdowns[rowKey] || packBreakdownByRow[rowKey];

      if (sale?.is_pack && Array.isArray(breakdown?.components)) {
        const packCourseIds = breakdown.components
          .map((component) => component?.course_id ?? productCourseMap.get(component?.product_id))
          .filter((id) => id !== null && id !== undefined);

        if (packCourseIds.length > 0) {
          return [...new Set(packCourseIds.map((id) => String(id)))].join(' - ');
        }
      }

      const fallbackCourseId = productCourseMap.get(sale?.product_id);
      return fallbackCourseId ? String(fallbackCourseId) : "";
    };

    const allocateCentsByWeight = (totalCents, weights) => {
      if (!Array.isArray(weights) || weights.length === 0) {
        return [];
      }

      const sign = totalCents < 0 ? -1 : 1;
      const absoluteTotal = Math.abs(Math.round(totalCents));
      const normalizedWeights = weights.map((weight) => Math.max(0, toFiniteNumber(weight, 0)));
      const weightSum = normalizedWeights.reduce((sum, weight) => sum + weight, 0);
      const effectiveWeights = weightSum > 0
        ? normalizedWeights
        : normalizedWeights.map(() => 1);
      const effectiveWeightSum = effectiveWeights.reduce((sum, weight) => sum + weight, 0);
      const rawAllocations = effectiveWeights.map((weight) => absoluteTotal * (weight / effectiveWeightSum));
      const allocations = rawAllocations.map((allocation) => Math.floor(allocation));
      let remainder = absoluteTotal - allocations.reduce((sum, allocation) => sum + allocation, 0);

      const fractionalOrder = rawAllocations
        .map((allocation, index) => ({ index, fraction: allocation - allocations[index] }))
        .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

      for (let index = 0; index < fractionalOrder.length && remainder > 0; index += 1) {
        allocations[fractionalOrder[index].index] += 1;
        remainder -= 1;
      }

      return allocations.map((allocation) => allocation * sign);
    };

    const packHeaders = [
      "Data",
      "ID Ordine",
      "ID Transazione",
      "ID Utente",
      "Nome Utente",
      "ID Pack",
      "Pack",
      "Tipo",
      "Metodo",
      "Codice Coupon",
      "ID Corso",
      "Corso",
      "ID Prodotto Incluso",
      "Prodotto Incluso",
      "Prezzo Storico Prodotto (€)",
      "Totale Prezzi Storici Pack (€)",
      "Fonte Prezzo",
      "Decorrenza Prezzo Storico",
      "Percentuale Revenue (%)",
      "Revenue Acquisto Pack (€)",
      "Revenue Corso (€)",
      "Stato Dettaglio"
    ];

    const packRows = packSales.flatMap((sale) => {
      const rowKey = getSaleRowKey(sale);
      const breakdown = fetchedBreakdowns[rowKey] || packBreakdownByRow[rowKey];
      const components = Array.isArray(breakdown?.components) ? breakdown.components : [];
      const saleAmountCents = Math.round(toFiniteNumber(sale?.amount, 0) * 100);

      const baseValues = [
        formatDateV2(sale.date, { day: "numeric", month: "numeric", year: "numeric" }),
        `#${sale.order_no}`,
        sale.transaction_id || "",
        sale.user_id,
        sale.user_name,
        sale.product_id,
        sanitizeCsvProductName(sale.product_name),
        getSaleTypeLabel(sale),
        getSaleMethodLabel(sale),
        sale.coupon?.code || ""
      ];

      if (components.length === 0) {
        return [[
          ...baseValues,
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          saleAmountCents / 100,
          "",
          "Dettaglio non disponibile"
        ]];
      }

      const coursesByKey = new Map();
      components.forEach((component) => {
        const courseKey = component?.course_id != null
          ? `course-${component.course_id}`
          : `product-${component?.product_id}`;
        const current = coursesByKey.get(courseKey) || {
          courseId: component?.course_id ?? "",
          courseName: component?.course_name || component?.product_name || "Corso non associato",
          weight: 0,
          referencePriceCents: 0,
          productIds: [],
          productNames: [],
          referenceSources: [],
          effectiveDates: [],
        };
        current.weight += Math.max(0, toFiniteNumber(component?.weight, 0));
        current.referencePriceCents += Math.max(0, toFiniteNumber(component?.reference_price_cents, 0));
        if (component?.product_id != null && !current.productIds.includes(component.product_id)) {
          current.productIds.push(component.product_id);
        }
        if (component?.product_name && !current.productNames.includes(component.product_name)) {
          current.productNames.push(component.product_name);
        }
        const referenceSource = getHistoricalPriceSourceLabel(component?.reference_source);
        if (referenceSource && !current.referenceSources.includes(referenceSource)) {
          current.referenceSources.push(referenceSource);
        }
        if (component?.effective_from && !current.effectiveDates.includes(component.effective_from)) {
          current.effectiveDates.push(component.effective_from);
        }
        coursesByKey.set(courseKey, current);
      });

      const courseRows = Array.from(coursesByKey.values());
      const totalReferencePriceCents = courseRows.reduce((sum, course) => sum + course.referencePriceCents, 0);
      const totalWeight = courseRows.reduce((sum, course) => sum + course.weight, 0);
      const effectiveWeights = totalWeight > 0
        ? courseRows.map((course) => course.weight)
        : courseRows.map(() => 1);
      const effectiveWeightSum = effectiveWeights.reduce((sum, weight) => sum + weight, 0);
      const courseAllocations = allocateCentsByWeight(saleAmountCents, effectiveWeights);

      return courseRows.map((course, index) => [
        ...baseValues,
        course.courseId,
        sanitizeCsvProductName(course.courseName),
        course.productIds.join(" - "),
        sanitizeCsvProductName(course.productNames.join(" - ")),
        course.referencePriceCents / 100,
        totalReferencePriceCents / 100,
        course.referenceSources.join(" - "),
        course.effectiveDates.join(" - "),
        Number(((effectiveWeights[index] / effectiveWeightSum) * 100).toFixed(4)),
        saleAmountCents / 100,
        courseAllocations[index] / 100,
        "OK"
      ]);
    });

    // Intestazioni del CSV
    const headers = [
      "Data",
      "ID Ordine",
      "ID Utente",
      "Nome Utente",
      "Prodotto",
      "ID Corsi",
      "Quantità",
      "Tipo",
      "Metodo",
      "Codice Coupon",
      "Tipo Coupon",
      "Valore Coupon",
      "Importo (€)",
      "Importo Totale (€)"
    ];

    // Dati del CSV
    const rows = visibleSales.map((sale) => {
      const quantity = toFiniteNumber(sale?.quantity, sale?.type === "refund" ? -1 : 1);
      const amount = toFiniteNumber(sale?.amount, 0);
      const totalAmount = toFiniteNumber(sale?.order_amount, amount);

      return [
        formatDateV2(sale.date, { day: "numeric", month: "numeric", year: "numeric" }),
        `#${sale.order_no}`,
        sale.user_id,
        sale.user_name,
        sanitizeCsvProductName(sale.product_name),
        getCourseIdsForSale(sale),
        quantity,
        getSaleTypeLabel(sale),
        getSaleMethodLabel(sale),
        sale.coupon ? sale.coupon.code : "",
        sale.coupon
          ? sale.coupon.amount_off
            ? 'Fisso'
            : 'Percentuale'
          : "",
        sale.coupon
          ? sale.coupon.amount_off
            ? (sale.coupon.amount_off / 100)
            : (sale.coupon.percent_off)
          : "",
        amount,
        totalAmount
      ];
    });

    // Combina intestazioni e righe
    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\n");

    const packCsvContent = [packHeaders, ...packRows]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\n");

    const downloadCsv = (filename, content) => {
      const blob = new Blob([`\uFEFF${content}`], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    downloadCsv("sales_report.csv", csvContent);
    downloadCsv("sales_pack_course_revenue.csv", packCsvContent);
  };

  const searchSales = (value) => {
    let cleanValue = value.trim().toLowerCase().replace(' ', '');
    if (cleanValue.length > 0) {
      setFilteredSales(sales.filter(sale => {
        return sale.order_no.toLowerCase().includes(cleanValue) ||
          sale.user_name.toLowerCase().includes(cleanValue) ||
          sale.product_name.toLowerCase().includes(cleanValue)
      }))
    }
    else {
      setFilteredSales(sales)
    }
  }

  const onSalesRowClick = useCallback(async (sale) => {
    if (!sale?.is_pack) {
      return;
    }

    const rowKey = `${sale.order_id}-${sale.product_id}-${sale.type}-${sale.transaction_id || 'na'}`;

    if (expandedPackRows[rowKey]) {
      setExpandedPackRows((prev) => ({ ...prev, [rowKey]: false }));
      return;
    }

    setExpandedPackRows((prev) => ({ ...prev, [rowKey]: true }));
    if (packBreakdownByRow[rowKey] || packBreakdownLoadingByRow[rowKey]) {
      return;
    }

    setPackBreakdownLoadingByRow((prev) => ({ ...prev, [rowKey]: true }));
    setPackBreakdownErrorByRow((prev) => ({ ...prev, [rowKey]: null }));
    try {
      const params = new URLSearchParams({
        order_id: String(sale.order_id),
        product_id: String(sale.product_id),
      });
      const response = await api.get(`/admin/reports/sales/transactions/pack-breakdown?${params.toString()}`);
      setPackBreakdownByRow((prev) => ({ ...prev, [rowKey]: response }));
    } catch (error) {
      console.error(error);
      setPackBreakdownErrorByRow((prev) => ({
        ...prev,
        [rowKey]: "Impossibile caricare il dettaglio del pack.",
      }));
    } finally {
      setPackBreakdownLoadingByRow((prev) => ({ ...prev, [rowKey]: false }));
    }
  }, [expandedPackRows, packBreakdownByRow, packBreakdownLoadingByRow]);

  const renderSalesMetricCard = ({ title, amountKey, progressiveKey, percentageDifference, gradientId }) => {
    const actualData = salesStats?.actual || [];
    const previousData = salesStats?.previous || [];
    const currentValue = actualData.length > 0 ? actualData[actualData.length - 1][progressiveKey] : 0;
    const previousValue = previousData.length > 0 ? previousData[previousData.length - 1][progressiveKey] : 0;
    const chartDataKey = salesView === 'trend' ? progressiveKey : amountKey;
    const maxChartValue = Math.max(
      0,
      ...actualData.map((item) => item?.[chartDataKey] || 0),
      ...previousData.map((item) => item?.[chartDataKey] || 0),
    );

    return (
      <Card appearance="modern" title={title}>
        <div className={styles.cardContent}>
          <div className={styles.amount}>
            {actualData.length > 0 && !loading.stats && (
              <div className={styles.actualAmount}>
                {formatPrice(currentValue)}€
                {percentageDifference !== null && (
                  <div className={`${styles.precentageBadge} ${percentageDifference < 0 ? styles.loss : ''}`}>
                    {percentageDifference > 0 ? '+' : ''}{percentageDifference.toFixed(0)}%
                  </div>
                )}
              </div>
            )}
            {previousData.length > 0 && !loading.stats && (
              <div className={styles.previousAmount}>
                {formatPrice(previousValue)}€ nel periodo precedente
              </div>
            )}
            {loading.stats && (
              <div className={styles.skeletonWrapper}>
                <Skeleton height={24} width={'70%'} borderRadius={8} />
                <Skeleton height={12} width={'100%'} borderRadius={8} />
              </div>
            )}
          </div>

          {loading.stats ? (
            <div className={styles.skeletonWrapper} style={{ minHeight: 180, display: 'flex', alignItems: 'stretch' }}>
              <Skeleton height={180} width={'100%'} borderRadius={12} />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <ComposedChart data={actualData} margin={{ top: 16, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid strokeWidth={1} stroke="#f5f5f5" horizontal={true} vertical={false} />
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tick={{
                    fill: 'rgba(var(--text-color-rgb), 44%)',
                    fontSize: '10px',
                    strokeWidth: 0
                  }}
                  tickFormatter={(value) => formatDate(value, salesPeriod)}
                />
                <YAxis
                  width={80}
                  hide={false}
                  yAxisId={gradientId}
                  domain={['auto', (dataMax) => Math.max(dataMax, maxChartValue)]}
                  axisLine={false}
                  tick={{
                    fill: 'rgba(var(--text-color-rgb), 44%)',
                    fontSize: '10px',
                    strokeWidth: 0
                  }}
                  tickFormatter={(value) => `€ ${formatPrice(value, 0)}`}
                />
                <Tooltip content={<CustomTooltip type="price" />} />
                <Area
                  type="monotone"
                  dataKey={chartDataKey}
                  stroke="var(--primary)"
                  fill={`url(#${gradientId})`}
                  yAxisId={gradientId}
                  activeDot={{ r: 4 }}
                  dot={false}
                />
                {previousData.length > 0 && (
                  <Line
                    type="monotone"
                    data={previousData}
                    dataKey={chartDataKey}
                    stroke="rgba(var(--text-color-rgb), .35)"
                    yAxisId={gradientId}
                    activeDot={{ r: 4 }}
                    dot={false}
                    strokeDasharray="3 3"
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
    );
  };

  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>
            Report - Vendite
          </title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <Breadcrumb items={[{ label: "Report" }, { label: "Vendite" }]} />
            <header className={styles.pageHeader}><h1>Report vendite</h1><p>Monitora venduto, incassi e transazioni nel periodo selezionato.</p></header>
            <div className={styles.filters}>
              <label className={styles.filterField}><span>Periodo di analisi</span><EasePicker options={options} placeholder="Seleziona Date"
                startDate={startDate ? new Date(startDate) : undefined}
                endDate={endDate ? new Date(endDate) : undefined}
                className={styles.datePicker} /></label>
              <div className={styles.filterField}><span>Prodotti</span><MultipleSelect appearance="modern" portal
                disabled={loading.courses}
                selected={products.filter(p => selectedProducts.includes(p.id))}
                options={filteredProducts}
                customOptions={[
                  {
                    id: "masters",
                    name: "Master",
                    selected: courses.filter(c => c.type === CourseType.Professional).map(c => c.product?.id).every(id => selectedProducts.includes(id)),
                    onSelect: (selected) => {
                      let masters_ids = courses.filter(c => c.type === CourseType.Professional).map(c => c.product?.id)
                      setSelectedProducts(products => {
                        return selected ? [...products, ...masters_ids] : products.filter(p => !masters_ids.includes(p))
                      })
                    }
                  },
                  {
                    id: "masterclass",
                    name: "Masterclass",
                    selected: courses.filter(c => c.type === CourseType.Masterclass).map(c => c.product?.id).every(id => selectedProducts.includes(id)),
                    onSelect: (selected) => {
                      let masters_ids = courses.filter(c => c.type === CourseType.Masterclass).map(c => c.product?.id)
                      setSelectedProducts(products => {
                        return selected ? [...products, ...masters_ids] : products.filter(p => !masters_ids.includes(p))
                      })
                    }
                  },
                  {
                    id: "bootcamp",
                    name: "Bootcamp",
                    selected: courses.find(c => c.type === CourseType.Bootcamp) ? courses.filter(c => c.type === CourseType.Bootcamp).map(c => c.product?.id).every(id => selectedProducts.includes(id)) : [],
                    onSelect: (selected) => {
                      let masters_ids = courses.filter(c => c.type === CourseType.Bootcamp).map(c => c.product?.id)
                      setSelectedProducts(products => {
                        return selected ? [...products, ...masters_ids] : products.filter(p => !masters_ids.includes(p))
                      })
                    }
                  }
                ]}
                allLabel="Tutti i prodotti"
                placeholder={"Seleziona Prodotti"}
                searchPlaceholder={"Cerca prodotto"}
                style={{ width: "100%" }}
                onSearch={(value) => setProductSearch(value ?? "")}
                onSelect={(items) => {
                  setSelectedProducts(items.map(item => item.id))
                }} /></div>
              <div className={styles.filterField}><span>Visualizzazione</span><Select appearance="modern" portal
                selected={salesViewOptions.find((item) => item.value === salesView)}
                options={salesViewOptions}
                placeholder={"Vista"}
                style={{ width: "100%" }}
                onSelect={(item) => {
                  setSalesView(item?.value || 'trend')
                }}
              /></div>
              <div className={styles.filterField}><span>Raggruppa per</span><Select appearance="modern" portal
                selected={salesPeriodOptions.find((item) => item.value === salesPeriod)}
                options={salesPeriodOptions}
                placeholder={"Periodo"}
                style={{ width: "100%" }}
                onSelect={(item) => {
                  setSalesPeriod(item?.value || 'day')
                }}
              /></div>

            </div>

            <div className={styles.legend}><span>Periodo selezionato</span><span>Periodo precedente</span></div>
            <div className={styles.metricsGrid}>
              {renderSalesMetricCard({
                title: 'VENDUTO',
                amountKey: 'total_amount',
                progressiveKey: 'total_progressive_amount',
                percentageDifference: totalSalesPercentageDifference,
                gradientId: 'soldGradient',
              })}
              {renderSalesMetricCard({
                title: 'INCASSATO',
                amountKey: 'amount',
                progressiveKey: 'progressive_amount',
                percentageDifference: suceededSalesPercentageDifference,
                gradientId: 'collectedGradient',
              })}
              {renderSalesMetricCard({
                title: 'IN ATTESA',
                amountKey: 'pending_amount',
                progressiveKey: 'pending_progressive_amount',
                percentageDifference: pendingSalesPercentageDifference,
                gradientId: 'pendingGradient',
              })}
              {renderSalesMetricCard({
                title: 'ABBANDONATO',
                amountKey: 'abandoned_amount',
                progressiveKey: 'abandoned_progressive_amount',
                percentageDifference: abandonedSalesPercentageDifference,
                gradientId: 'abandonedGradient',
              })}
            </div>

            <div className={styles.transactionsSection}>
              <Card appearance="modern" integratedTable title="Transazioni" actions={<div className={styles.transactionActions}><span className={styles.resultCount}>{loading.transactions ? 'Caricamento…' : `${filteredSales.filter(sale => isVisibleTransactionStatus(sale.status)).length} transazioni`}</span><Button appearance="subtle" additionalClass={styles.exportButton} onClick={exportSalesToCSV}><ExportIcon aria-hidden="true" style={{ width: 11, height: 11 }} />Esporta</Button></div>}>
              <div className={styles.searchToolbar}><TextInput appearance="modern" type="search" placeholder="Cerca per ID, nome utente o prodotto" onKeyUp={searchSales} /></div>
              <Table
                appearance="minimal"
                style={{ border: 0, borderRadius: 0, marginTop: 0 }}
                loading={loading.transactions}
                loadingN={25}
                onRowClick={onSalesRowClick}
                columns={[
                  { key: 'date', label: 'DATA', type: 'date' },
                  { key: 'order_no', label: 'ID ORDINE', type: 'string' },
                  { key: 'user_name', label: 'UTENTE', type: 'string' },
                  { key: 'product_name', label: 'PRODOTTO', type: 'custom', wrap: true },
                  { key: 'quantity', label: 'QUANTITÀ', type: 'number' },
                  { key: 'type', label: 'TIPO', type: 'custom' },
                  { key: 'method', label: 'METODO', type: 'string' },
                  { key: 'amount', label: 'IMPORTO', type: 'custom' },
                  { key: 'coupon', label: 'COUPON', type: 'custom' },

                ]}
                elements={filteredSales.filter(sale => isVisibleTransactionStatus(sale.status)).map((sale) => (
                  {
                    ...sale,
                    type: { value: sale.type, type: 'string', component: <StatusBadge appearance="soft" {...getTypeBadge(sale.type)} /> },
                    method: sale.transaction_id ? 'Stripe' : 'Manuale',
                    product_name: {
                      value: sale.product_name, type: 'string', component: (() => {
                        const rowKey = `${sale.order_id}-${sale.product_id}-${sale.type}-${sale.transaction_id || 'na'}`;
                        const isExpanded = Boolean(expandedPackRows[rowKey]);
                        const isLoading = Boolean(packBreakdownLoadingByRow[rowKey]);
                        const breakdownError = packBreakdownErrorByRow[rowKey];
                        const breakdown = packBreakdownByRow[rowKey];

                        return (
                          <div className={styles.saleProduct}>
                            <div>
                              {sale.product_name}
                              {sale.is_pack && <span className={styles.couponAmount} style={{ marginLeft: '.5rem' }}>(Pack - clicca per dettaglio)</span>}
                            </div>
                            {sale.is_pack && isExpanded && (
                              <div style={{ marginTop: '.5rem', fontSize: '.85rem', lineHeight: 1.35 }}>
                                {isLoading && <div>Caricamento dettaglio...</div>}
                                {!isLoading && breakdownError && <div style={{ color: 'var(--sf-red)' }}>{breakdownError}</div>}
                                {!isLoading && !breakdownError && breakdown && (
                                  <>
                                    <div style={{ marginBottom: '.35rem' }}>
                                      Importo pack: <strong>{formatPrice((breakdown.pack_amount_cents || 0) / 100)}€</strong>
                                    </div>
                                    {(breakdown.components || []).map((component) => (
                                      <div key={`${rowKey}-${component.product_id}`} style={{ display: 'flex', justifyContent: 'space-between', gap: '.75rem' }}>
                                        <span>{component.product_name}</span>
                                        <span>{((component.weight || 0) * 100).toFixed(2)}% • <strong>{formatPrice((component.allocated_amount_cents || 0) / 100)}€</strong></span>
                                      </div>
                                    ))}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()
                    },
                    amount: {
                      value: sale.amount, type: 'number', component:
                        <div className={`${styles.saleAmount} ${styles[sale.type]}`}>
                          {formatPrice(sale.amount)}€
                        </div>
                    },
                    coupon: {
                      value: sale.coupon ? sale.coupon.code : '', type: 'string', component: <div className={styles.saleCoupon}>
                        {sale.coupon?.code}
                        {sale.coupon?.amount_off && <span className={styles.couponAmount}>(-{formatPrice(sale.coupon.amount_off / 100)}€)</span>}
                        {sale.coupon?.percent_off && <span className={styles.couponAmount}>(-{sale.coupon.percent_off}%)</span>}
                      </div>
                    }
                  }
                ))}
              />
              </Card>
            </div>
          </div>
        </div>
      </div >
    </HeaderFooterLayout >

  );
};

export default Reports;
